import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Globe, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function CercaRecapitiDialog({ medico, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [comune, setComune] = useState('Celano');
  const [esito, setEsito] = useState(null);
  const [form, setForm] = useState({ telefono: '', email: '', indirizzo_ambulatorio: '' });
  const [saving, setSaving] = useState(false);

  const cerca = async () => {
    setLoading(true);
    setEsito(null);
    try {
      const r = await api.post(`/utility/mmg/${medico.id}/cerca-recapiti`, { comune });
      setEsito(r.data);
      const p = r.data.proposta || {};
      setForm({
        telefono: p.telefono || medico.telefono || '',
        email: p.email || medico.email || '',
        indirizzo_ambulatorio: p.indirizzo_ambulatorio || medico.indirizzo_ambulatorio || '',
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Ricerca non riuscita'); }
    setLoading(false);
  };

  const salva = async () => {
    setSaving(true);
    try {
      await api.put(`/utility/mmg/${medico.id}`, {
        cognome: medico.cognome, nome: medico.nome,
        telefono: form.telefono, email: form.email,
        indirizzo_ambulatorio: form.indirizzo_ambulatorio,
        codice_regionale: medico.codice_regionale || '',
      });
      toast.success('Recapiti confermati e salvati');
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSaving(false);
  };

  const p = esito?.proposta || {};

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand" />Cerca recapiti - Dott. {medico?.cognome} {medico?.nome}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500 leading-relaxed">
            La ricerca sul web restituisce solo una proposta con le fonti: nulla viene salvato senza conferma.
            Il dato comunicato dal medico stesso vale piu di qualsiasi elenco.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Comune dell'ambulatorio</Label>
              <Input className="h-9 text-xs" value={comune} onChange={e => setComune(e.target.value)} data-testid="recapiti-comune" />
            </div>
            <Button className="h-9 bg-brand hover:bg-brand-dark text-white" onClick={cerca} disabled={loading} data-testid="recapiti-cerca">
              {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Ricerca...</> : 'Cerca sul web'}
            </Button>
          </div>

          {esito && (
            <div className="space-y-3 border-t pt-3" data-testid="recapiti-esito">
              <p className="text-[11px] text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />{esito.avviso} Attendibilita dichiarata: {p.attendibilita || 'non indicata'}.
              </p>
              {p.note && <p className="text-xs text-slate-500">{p.note}</p>}
              <div className="space-y-2">
                <div><Label className="text-xs">Telefono</Label>
                  <Input className="h-9 text-xs" value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} data-testid="recapiti-telefono" /></div>
                <div><Label className="text-xs">Email</Label>
                  <Input className="h-9 text-xs" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="recapiti-email" /></div>
                <div><Label className="text-xs">Indirizzo ambulatorio</Label>
                  <Input className="h-9 text-xs" value={form.indirizzo_ambulatorio} onChange={e => setForm(f => ({ ...f, indirizzo_ambulatorio: e.target.value }))} data-testid="recapiti-indirizzo" /></div>
                {p.orari && <p className="text-xs text-slate-500">Orari trovati: {p.orari}</p>}
              </div>
              {(p.fonti || []).length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Fonti</p>
                  <ul className="space-y-0.5">
                    {p.fonti.map((f, i) => (
                      <li key={i} className="text-[11px]">
                        <a href={f.url} target="_blank" rel="noreferrer" className="text-brand hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />{f.titolo || f.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Chiudi senza salvare</Button>
                <Button className="bg-brand hover:bg-brand-dark text-white" onClick={salva} disabled={saving} data-testid="recapiti-salva">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Conferma e salva
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
